import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import type { FeedItem, Member } from '../api.js';
import { t } from '../i18n.js';
import { formatDate } from '../format.js';
import {
  albums,
  edgesOfGroup,
  form,
  feed,
  feedBusy,
  feedCursor,
  groups,
  me,
  members,
  view,
  type GroupTab,
} from '../state.js';
import { loadFeed, onOpenAlbum, onOpenFeedItem, onRemoveMember, openView } from '../actions.js';
import { Action, Avatar, Empty, IconButton, Pill } from './parts.js';
import {
  IconCollection,
  IconFeed,
  IconMembers,
  IconNote,
  IconPlus,
  IconStarFilled,
  IconTrash,
} from './icons.js';
import { Thumb, filmstripOf } from './gallery.js';
import { MarkdownExcerpt } from '../markdown.js';

/**
 * **Un groupe** — *qui est là, et qu'est-ce qui y circule ?*
 *
 * ### L'audience d'abord
 *
 * Un groupe **est** des gens ; ce qui y passe vient après. C'est pourquoi la
 * création atterrit sur *Membres* et non sur *Collections* : un cercle vide n'a
 * rien à partager, et la première chose à faire est d'y inviter quelqu'un. C'est
 * aussi ce qui dure — les collections passent, l'audience reste.
 *
 * ### Trois faces, et aucune n'est un type d'objet
 *
 * *Collections* est **ce qu'on donne** ; *Nouveautés* **ce qui arrive** ;
 * *Membres* **à qui**. La V1 opposait « Reçu » et « Groupes », c'est-à-dire une
 * direction et un objet — ce qui laissait croire à deux inventaires là où il n'y
 * en a qu'un. La direction inverse, *à qui ai-je donné ceci ?*, n'est pas une
 * liste : c'est une propriété d'objet, et elle se lit dans l'inspecteur.
 *
 * ### Ce qu'on ne peut pas faire ici, et pourquoi
 *
 * On ne partage pas un fichier depuis cet écran. Le socle n'a **aucune** route
 * pour exposer une entité seule : le seul partage positif est
 * `POST /albums/{aid}/groups`. L'absence du geste dit la règle mieux qu'une
 * explication — on met dans une collection, et c'est la collection qui circule.
 */
export function GroupView(): JSX.Element | null {
  const v = view.value;
  if (v.kind !== 'group') return null;

  const group = (groups.value ?? []).find((g) => g.gid === v.gid);
  const roster = members.value[v.gid];
  const shared = edgesOfGroup(v.gid);

  return (
    <div class="group" data-testid={`group-view-${v.gid}`}>
      <nav class="tabs" role="tablist" aria-label={group?.name ?? t('nav.shared')}>
        <Tab gid={v.gid} tab="collections" current={v.tab} icon={<IconCollection size={16} />}>
          {t('group.collections')}
        </Tab>
        <Tab
          gid={v.gid}
          tab="feed"
          current={v.tab}
          icon={<IconFeed size={16} />}
          // Le compte vient du **miroir**, jamais du fil chargé : il doit être là
          // avant qu'on ouvre l'onglet, sinon l'onglet est le seul endroit d'où
          // apprendre qu'il fallait l'ouvrir.
          count={shared.length}
        >
          {t('group.feed')}
        </Tab>
        <Tab
          gid={v.gid}
          tab="members"
          current={v.tab}
          icon={<IconMembers size={16} />}
          count={roster?.length ?? group?.memberCount}
        >
          {t('group.members')}
        </Tab>
      </nav>

      {v.tab === 'collections' ? <Collections gid={v.gid} /> : null}
      {v.tab === 'feed' ? <Feed gid={v.gid} /> : null}
      {v.tab === 'members' ? <Members gid={v.gid} /> : null}
    </div>
  );
}

function Tab({
  gid,
  tab,
  current,
  icon,
  count,
  children,
}: {
  readonly gid: string;
  readonly tab: GroupTab;
  readonly current: GroupTab;
  readonly icon: JSX.Element;
  readonly count?: number;
  readonly children: JSX.Element | string;
}): JSX.Element {
  const active = current === tab;
  return (
    <button
      type="button"
      role="tab"
      class={active ? 'tab current' : 'tab'}
      data-testid={`tab-${tab}`}
      aria-selected={active}
      onClick={() => openView({ kind: 'group', gid, tab })}
    >
      {icon}
      <span>{children}</span>
      {count !== undefined && count > 0 ? <span class="tab-count">{count}</span> : null}
    </button>
  );
}

/**
 * **Les collections du groupe.**
 *
 * `authGroups` est filtré au point de vue de l'appelant par le serveur : un membre
 * n'y voit que les groupes qu'il partage avec le propriétaire, jamais les autres
 * cercles de celui-ci. Croiser côté client est donc sûr — et c'est la seule façon
 * d'y arriver sans une route par groupe, `GET /albums` rendant déjà l'union des
 * siennes et de celles qu'on lui partage.
 */
function Collections({ gid }: { readonly gid: string }): JSX.Element {
  const mine = (albums.value ?? []).filter((a) => (a.authGroups ?? []).includes(gid));

  return (
    <div class="panel">
      {mine.length === 0 ? (
        <Empty action={<AddHint kind="collection" />}>{t('collection.none')}</Empty>
      ) : (
        <ul class="cards" data-testid={`collections-${gid}`}>
          {mine.map((a) => (
            <li key={a.Aid} class="card">
              <button
                type="button"
                class="card-open"
                data-testid={`collection-${a.Aid}`}
                onClick={() => void onOpenAlbum(String(a.Aid))}
              >
                <IconCollection size={20} />
                <span class="grow">
                  <span class="card-title">{a.Name}</span>
                  <span class="card-meta">{t('collection.itemCount', { count: a.ItemCount ?? 0 })}</span>
                </span>
                {/* **Une collection collective se signale** *(2026-08-16)*. Sans
                    marque, un membre n'a aucun moyen d'apprendre qu'il peut y
                    verser : le sélecteur la lui propose depuis toujours, et
                    l'ajout échouait en `denied` tant que la politique valait
                    `owner`. Le réglage existe désormais ; encore faut-il que
                    ceux à qui il ouvre la porte la voient. */}
                {a.ContribPolicy === 'members' ? (
                  <Pill tone="share" testId={`collective-${a.Aid}`}>
                    {t('collection.collective')}
                  </Pill>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      )}

    </div>
  );
}

/**
 * Le renvoi vers le « + ». Un état vide sans porte de sortie oblige à chercher où
 * l'on ajoute ; le dire une fois, à l'endroit du manque, suffit — et le geste reste
 * **le même partout**, ce qui est tout l'intérêt d'un point d'ajout unique.
 */
function AddHint({ kind }: { readonly kind: 'collection' | 'invite' }): JSX.Element {
  return (
    <Action testId={`hint-${kind}`} variant="quiet" onClick={() => (form.value = kind)}>
      <IconPlus size={16} />
      <span>{kind === 'invite' ? t('group.invite') : t('collection.newName')}</span>
    </Action>
  );
}

/**
 * **Les nouveautés** — `GET /groups/{gid}/feed`.
 *
 * Ce fil est **affichable sans aucune autre lecture** (D28) : chaque arête porte
 * titre, état, `Variants`, `Excerpt` et l'agrégat de réactions. Sans la projection,
 * un membre recevait trois cents références nues qu'aucune route en lot ne
 * complétait — et la seule issue était trois cents appels unitaires.
 *
 * Un tombstone y figure aussi, et c'est voulu : le retrait *est* ce qu'un membre
 * vient chercher. Il se rend en creux plutôt qu'en absence — une ligne qui
 * disparaît sans rien dire se lit comme un bogue d'affichage.
 */
function Feed({ gid }: { readonly gid: string }): JSX.Element {
  const items = feed.value;

  if (items.length === 0) {
    return feedBusy.value ? (
      <p class="notice busy" role="status">
        <span class="spinner" aria-hidden="true" />
      </p>
    ) : (
      <Empty>{t('group.emptyFeed')}</Empty>
    );
  }

  return (
    <div class="panel">
      <ul class="feed" data-testid={`feed-${gid}`}>
        {items.map((item) => (
          <FeedRow key={`${item.Fid ?? item.Aid}`} item={item} gid={gid} />
        ))}
      </ul>
      {feedCursor.value !== null ? (
        <Action testId="feed-more" onClick={() => void loadFeed(gid, true)}>
          {t('group.more')}
        </Action>
      ) : null}
    </div>
  );
}

/**
 * Une ligne du fil, **et elle s'ouvre** *(2026-08-16)*.
 *
 * Elle ne s'ouvrait pas : le contenu reçu ne s'atteignait que par *Mes partages →
 * groupe → Collections*, c'est-à-dire en repassant par l'ensemble d'où il venait.
 * Le fil annonçait donc des choses qu'on ne pouvait pas regarder depuis l'endroit
 * qui les annonçait — la seule liste de l'application dont les lignes étaient
 * inertes.
 *
 * Le bouton **enveloppe le visuel et le texte, pas les réactions** : celles-ci
 * sont une lecture, et les enfermer dans un bouton ferait d'un `<Pill>` un
 * élément interactif imbriqué — le clic partirait au mauvais des deux.
 */
function FeedRow({ item, gid }: { readonly item: FeedItem; readonly gid: string }): JSX.Element {
  const id = item.Fid ?? item.Aid ?? '';
  const score =
    item.RxnAgg && item.RxnAgg.ScoreCount > 0
      ? item.RxnAgg.ScoreSum / item.RxnAgg.ScoreCount
      : undefined;
  const thumbable = item.Variants?.['thumb']?.size !== undefined;

  return (
    <li class="feed-row" data-testid={`feed-item-${id}`}>
      <button
        type="button"
        class="feed-open"
        data-testid={`open-feed-${id}`}
        // Un retrait n'a plus d'octets derrière lui : la ligne reste — c'est ce
        // qu'un membre vient lire — mais elle ne mène nulle part.
        disabled={item.Deleted === true}
        aria-label={item.Name}
        onClick={() => onOpenFeedItem(item, feed.value)}
      >
      <div class="feed-visual">
        {item.Deleted ? (
          <div class="tile-placeholder" data-testid={`feed-gone-${id}`} title={t('group.revoked')}>
            ∅
          </div>
        ) : thumbable ? (
          // Le `gid` est un **indice de résolution**, jamais un droit : il épargne
          // au serveur de sonder les groupes de l'appelant pour un `fid` qu'il ne
          // possède pas. La décision reste portée par l'arête.
          <Thumb fid={id} alt={item.Name} gid={gid} strip={filmstripOf(item)} />
        ) : item.Excerpt ? (
          /* **Le papier, ici aussi** *(2026-08-16)*. La note portait dans le fil
             son ancien rendu — un texte gris sur l'aplat de la case — alors que la
             même note, ouverte depuis la collection, s'affiche sur son papier. Deux
             styles pour la même chose selon l'écran d'où on la regarde : c'est
             exactement ce que `.paper` a été extrait pour éviter, et le fil avait
             été oublié. */
          <div class="feed-excerpt paper">
            <MarkdownExcerpt source={item.Excerpt} testId={`feed-excerpt-${id}`} />
          </div>
        ) : (
          <div class="tile-placeholder tile-kind">
            {item.Aid ? <IconCollection size={20} /> : <IconNote size={20} />}
          </div>
        )}
      </div>

      <div class="feed-text">
        <span class="feed-name">{item.Name}</span>
        <span class="feed-meta">
          {/* **Qui l'a mis là.** `Owner` seul est un ULID opaque : « quelqu'un a
              ajouté une photo » n'est pas un fil. L'alias est calculé à la
              réponse, et vaut `null` pour qui n'en a pas choisi. */}
          <span data-testid={`feed-author-${id}`}>{item.ownerAlias ?? t('group.someone')}</span>
          {item.EdgeUpdatedAt ? <span> · {formatDate(item.EdgeUpdatedAt)}</span> : null}
        </span>
        {item.Deleted ? <Pill tone="warning">{t('group.revoked')}</Pill> : null}
      </div>
      </button>

      {score !== undefined || Object.keys(item.RxnAgg?.Emoji ?? {}).length > 0 ? (
        <div class="feed-rxn" data-testid={`feed-rxn-${id}`}>
          {score !== undefined ? (
            <Pill tone="share">
              <IconStarFilled size={13} />
              {score.toFixed(1)}
            </Pill>
          ) : null}
          {Object.entries(item.RxnAgg?.Emoji ?? {}).map(([symbol, count]) => (
            <Pill key={symbol}>
              {symbol} {count}
            </Pill>
          ))}
        </div>
      ) : null}
    </li>
  );
}

/**
 * **Les membres**, nommés.
 *
 * L'alias est arrivé avec cet incrément : la route ne rendait que des `uid`, et
 * une liste d'ULID n'est pas une liste de personnes — on n'exclut pas quelqu'un
 * d'un cercle sur la foi d'un identifiant opaque.
 *
 * **Aucune adresse n'est jamais affichée** (I-7) : l'email sert à inviter, il
 * n'est pas stocké sur l'appartenance et ne revient sur aucune réponse. Un listing
 * qui les rendrait ferait de toute appartenance un annuaire.
 */
function Members({ gid }: { readonly gid: string }): JSX.Element {
  const roster = members.value[gid];
  const mine = me.value?.uid;
  const owner = roster?.some((m) => m.uid === mine && m.role === 'owner') ?? false;

  return (
    <div class="panel">
      {owner && (roster?.length ?? 0) <= 1 ? (
        <Empty action={<AddHint kind="invite" />}>{t('group.aloneHere')}</Empty>
      ) : null}

      {roster === undefined ? (
        <p class="notice busy" role="status">
          <span class="spinner" aria-hidden="true" />
        </p>
      ) : (
        <ul class="members" data-testid={`members-${gid}`}>
          {roster.map((m) => (
            <MemberRow key={m.uid} member={m} gid={gid} canRemove={owner} isMe={m.uid === mine} />
          ))}
        </ul>
      )}
    </div>
  );
}

function MemberRow({
  member,
  gid,
  canRemove,
  isMe,
}: {
  readonly member: Member;
  readonly gid: string;
  readonly canRemove: boolean;
  readonly isMe: boolean;
}): JSX.Element {
  const [confirming, setConfirming] = useState(false);
  // Le propriétaire ne se retire pas lui-même : le serveur refuse en `409
  // last_owner`, et un groupe sans propriétaire serait un objet que plus personne
  // ne peut administrer. Proposer le geste pour le voir échouer serait pire que
  // de ne pas le proposer.
  const removable = canRemove && !isMe && member.role !== 'owner';

  return (
    <li class="member" data-testid={`member-${member.uid}`}>
      {/* **Un visage plutôt que deux lettres, quand il y en a un** *(2026-08-16)*.
          C'est ici que l'avatar prend son sens : la liste des membres est
          l'endroit où l'on reconnaît quelqu'un. `avatarRev` est servi **avec la
          liste** — sans lui, peindre cinquante membres demanderait cinquante
          requêtes pour apprendre que quarante-huit n'ont pas de photo. */}
      <Avatar
        uid={member.uid}
        alias={member.alias ?? undefined}
        rev={member.avatarRev}
        testId={`avatar-${member.uid}`}
      />
      <span class="grow">
        <span class="member-name" data-testid={`member-name-${member.uid}`}>
          {member.alias ?? t('group.unnamed')}
          {isMe ? ` · ${t('group.you')}` : ''}
        </span>
        <span class="member-meta mono">{member.uid}</span>
      </span>
      <Pill tone={member.role === 'owner' ? 'accent' : 'neutral'}>{roleLabel(member.role)}</Pill>
      {removable ? (
        confirming ? (
          <span class="confirm">
            <Action
              testId={`remove-member-${member.uid}`}
              variant="danger"
              onClick={() => void onRemoveMember(gid, member.uid)}
            >
              {t('group.confirmRemove')}
            </Action>
            <Action testId={`cancel-remove-${member.uid}`} variant="quiet" onClick={() => setConfirming(false)}>
              {t('app.cancel')}
            </Action>
          </span>
        ) : (
          <IconButton
            testId={`ask-remove-${member.uid}`}
            label={t('group.remove', { name: member.alias ?? member.uid })}
            onClick={() => setConfirming(true)}
          >
            <IconTrash size={18} />
          </IconButton>
        )
      ) : null}
    </li>
  );
}

/** Un rôle inconnu se rend **tel quel** plutôt qu'en clé de traduction crue. */
export function roleLabel(role: string): string {
  const key = `shared.role.${role}`;
  const label = t(key);
  return label === key ? role : label;
}
